/**
 * component-cart-recos-scroll.js
 * Cart drawer recommendations — drag-to-scroll desktop + dots pagination.
 * Iter 2026-05-08 follow-up — Item #1 (feedback Guillaume).
 *
 * Remplace le chevron click-through (qui captait le clic puis propageait
 * sur la card en-dessous) par une UX moderne :
 *   - Drag-to-scroll souris (cursor: grab → grabbing pendant drag)
 *   - Dots pagination en bas, hors zone des cards (non-ambigu)
 *   - Threshold 5px de mouvement pour distinguer drag vs click
 *   - Touch scroll natif conservé (mobile)
 *
 * Mécanique :
 *   - mousedown : startX = e.pageX, scrollLeft = container.scrollLeft, isDown = true
 *   - mousemove : si isDown, container.scrollLeft = scrollLeft - (e.pageX - startX) * 1.2
 *     (sensitivity 1.2× pour glide naturel)
 *   - mouseup / mouseleave : isDown = false
 *   - Click guard : si distance dragged > 5px → on ajoute class .is-dragging qui
 *     bloque pointer-events sur les liens enfants pendant 50ms (anti-faux-click)
 *
 * Dots :
 *   - 1 dot par card (data-reco-card)
 *   - sur scroll event → calcule l'index visible (scrollLeft / cardWidth)
 *   - dot.is-active suit l'index courant
 *   - click dot → scrollTo({ left: i * cardWidth, behavior: 'smooth' })
 *
 * Re-init : observe le container `[data-cart-recos-list]` via MutationObserver
 * sur sa propriété childList car les cards sont injectées async (hydratation
 * recommendations) ou via Section Rendering API (re-render après cart change).
 */
(function () {
  'use strict';

  var DRAG_THRESHOLD = 5; // px — au-delà, on considère c'est un drag (pas un click)
  var SENSITIVITY = 1.2;
  var DOTS_CLEAR_DELAY = 50; // ms — délai pour retirer is-dragging après mouseup

  function initRecosScroll(container) {
    if (!container || container.dataset.recosScrollInit === '1') return;
    container.dataset.recosScrollInit = '1';

    var isDown = false;
    var startX = 0;
    var startScrollLeft = 0;
    var totalDelta = 0;

    function onWindowMouseMove(e) {
      if (!isDown) return;
      var x = e.pageX - container.offsetLeft;
      var walk = (x - startX) * SENSITIVITY;
      totalDelta = Math.abs(walk);
      if (totalDelta > DRAG_THRESHOLD && !container.classList.contains('is-dragging')) {
        container.classList.add('is-dragging');
      }
      e.preventDefault();
      container.scrollLeft = startScrollLeft - walk;
    }

    function onWindowMouseUp() {
      if (!isDown) return;
      isDown = false;
      window.removeEventListener('mousemove', onWindowMouseMove);
      window.removeEventListener('mouseup', onWindowMouseUp);
      _endDrag(container);
    }

    container.addEventListener('mousedown', function (e) {
      // Ignore right-click + middle-click
      if (e.button !== 0) return;
      isDown = true;
      totalDelta = 0;
      startX = e.pageX - container.offsetLeft;
      startScrollLeft = container.scrollLeft;
      // Listeners au niveau window pour continuer à drag même si la souris sort
      // du container (UX standard ; sinon le drag se coupe dès qu'on dépasse les
      // bords du drawer recos qui est petit ~84px de haut).
      window.addEventListener('mousemove', onWindowMouseMove);
      window.addEventListener('mouseup', onWindowMouseUp);
    });

    // Click guard : si on a dragged > threshold, on consomme le 1er click qui suit
    // (capture phase) pour empêcher la nav vers la card produit sous le curseur.
    container.addEventListener('click', function (e) {
      if (totalDelta > DRAG_THRESHOLD) {
        e.stopPropagation();
        e.preventDefault();
        totalDelta = 0;
      }
    }, true); // useCapture pour intercepter avant les listeners enfants

    // Build dots + sync on scroll
    _buildDots(container);
    container.addEventListener('scroll', _onScroll.bind(null, container), { passive: true });
  }

  function _endDrag(container) {
    // Retire is-dragging avec un petit délai pour laisser passer le click guard
    setTimeout(function () {
      container.classList.remove('is-dragging');
    }, DOTS_CLEAR_DELAY);
  }

  function _buildDots(container) {
    // Le container des dots est un sibling — on le cherche dans le parent
    // (data-cart-recos block).
    var recosBlock = container.closest('[data-cart-recos]');
    if (!recosBlock) return;
    var dotsEl = recosBlock.querySelector('[data-cart-recos-dots]');
    if (!dotsEl) return;

    var cards = container.querySelectorAll('[data-reco-card]');
    var count = cards.length;
    dotsEl.dataset.count = String(count);

    // Clear existing dots
    while (dotsEl.firstChild) dotsEl.removeChild(dotsEl.firstChild);

    if (count <= 1) return; // Pas de pagination si 0 ou 1 card

    for (var i = 0; i < count; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'edj-cart-drawer__recos-dot';
      dot.setAttribute('aria-label', 'Aller à la suggestion ' + (i + 1));
      dot.dataset.dotIndex = String(i);
      if (i === 0) dot.classList.add('is-active');
      (function (idx) {
        dot.addEventListener('click', function () {
          var card = container.querySelectorAll('[data-reco-card]')[idx];
          if (!card) return;
          // scrollIntoView avec inline:start respecte le scroll-padding du container
          container.scrollTo({
            left: card.offsetLeft - container.offsetLeft - 22, // -22 = padding-left
            behavior: 'smooth',
          });
        });
      })(i);
      dotsEl.appendChild(dot);
    }
  }

  var scrollRaf = null;
  function _onScroll(container) {
    if (scrollRaf) return;
    scrollRaf = requestAnimationFrame(function () {
      scrollRaf = null;
      _updateActiveDot(container);
    });
  }

  function _updateActiveDot(container) {
    var recosBlock = container.closest('[data-cart-recos]');
    if (!recosBlock) return;
    var dotsEl = recosBlock.querySelector('[data-cart-recos-dots]');
    if (!dotsEl) return;
    var dots = dotsEl.querySelectorAll('.edj-cart-drawer__recos-dot');
    if (dots.length === 0) return;

    var cards = container.querySelectorAll('[data-reco-card]');
    if (cards.length === 0) return;

    // Determine la card visible : la 1ère dont le offsetLeft >= scrollLeft
    var scrollLeft = container.scrollLeft;
    var activeIdx = 0;
    for (var i = 0; i < cards.length; i++) {
      var cardLeft = cards[i].offsetLeft - container.offsetLeft;
      // tolerance 30px pour ne pas flicker entre 2 dots quand on est à mi-chemin
      if (scrollLeft + 30 >= cardLeft) {
        activeIdx = i;
      }
    }

    dots.forEach(function (dot, idx) {
      if (idx === activeIdx) {
        dot.classList.add('is-active');
      } else {
        dot.classList.remove('is-active');
      }
    });
  }

  // ── Bootstrap & re-init ──
  function initAll(root) {
    var scope = root || document;
    var lists = scope.querySelectorAll('[data-cart-recos-list]');
    lists.forEach(initRecosScroll);
  }

  // Observe l'injection async de cards dans le container (hydratation recos
  // ou re-render via Section Rendering API).
  function watchListInjection(list) {
    if (list.dataset.recosObserverInit === '1') return;
    list.dataset.recosObserverInit = '1';
    var observer = new MutationObserver(function () {
      _buildDots(list);
      _updateActiveDot(list);
    });
    observer.observe(list, { childList: true });
  }

  function bootstrap() {
    initAll(document);
    document.querySelectorAll('[data-cart-recos-list]').forEach(watchListInjection);
    // Force build dots on existing lists (cas où cards déjà injectées avant init)
    document.querySelectorAll('[data-cart-recos-list]').forEach(function (list) {
      _buildDots(list);
      _updateActiveDot(list);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }

  // Re-init après re-render du drawer (event custom dispatché par edj-cart-drawer.js
  // après un fetch de section, OU via shopify:section:load).
  document.addEventListener('cart:updated', function () {
    setTimeout(bootstrap, 100);
  });

  // Re-init après hydratation des recos (event custom dispatché par
  // edj-cart-drawer.js après injection des cards).
  document.addEventListener('cart:recos-hydrated', function () {
    setTimeout(bootstrap, 50);
  });

  // Watch global pour de nouveaux containers ajoutés par re-render section.
  // Ex : Section Rendering API remplace tout le drawer content → l'élément
  // [data-cart-recos-list] précédemment référencé est détruit, un NOUVEAU
  // est créé. On doit le détecter et l'init.
  var globalObserver = new MutationObserver(function (mutations) {
    var needsInit = false;
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      for (var j = 0; j < m.addedNodes.length; j++) {
        var n = m.addedNodes[j];
        if (n.nodeType !== 1) continue;
        if (n.matches && n.matches('[data-cart-recos-list]')) { needsInit = true; break; }
        if (n.querySelector && n.querySelector('[data-cart-recos-list]')) { needsInit = true; break; }
      }
      if (needsInit) break;
    }
    if (needsInit) {
      setTimeout(bootstrap, 30);
    }
  });
  globalObserver.observe(document.body, { childList: true, subtree: true });

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector && e.target.querySelector('[data-cart-recos-list]')) {
      bootstrap();
    }
  });
})();
